import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, getDefaultRouteForUser, getUserAccessState } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Eye, EyeOff, LogIn, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error: authError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'staff',
  });
  const [validation, setValidation] = useState({
    email: '',
    password: '',
  });

  // Auto-dismiss toast after 4 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Show auth errors as toast
  useEffect(() => {
    if (authError) {
      setToast({
        type: 'error',
        message: authError === 'Login failed' ? 'Password incorrect. Please try again.' : authError,
      });
    }
  }, [authError]);

  useEffect(() => {
    const hasActivationParams = searchParams.has('token') || searchParams.has('code') || searchParams.get('type') === 'recovery';
    if (!hasActivationParams) return;

    navigate(`/activate-account?${searchParams.toString()}`, { replace: true });
  }, [navigate, searchParams]);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!formData.email.includes('@')) {
      errors.email = 'Invalid email format';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await login(formData.email, formData.password);
    
    if (result?.success && result?.user) {
      const userRole = result.user.role || 'staff';
      localStorage.setItem('user_role', userRole);
      localStorage.setItem('rtb_user_role', userRole);
      localStorage.setItem('user', JSON.stringify(result.user));

      const accessState = getUserAccessState(result.user);
      let destination = getDefaultRouteForUser(result.user);
      if (accessState === 'pending_activation') {
        destination = '/activate-account';
      } else if (accessState === 'disabled') {
        destination = '/account-disabled';
      }
      
      setToast({
        type: 'success',
        message: `Welcome, ${result.user.name}!`,
      });
      
      // Redirect after a short delay to show success message
      setTimeout(() => navigate(destination), 1000);
    } else {
      // Handle all error cases
      setToast({
        type: 'error',
        message: result?.error || 'Authentication failed. Please check your credentials.',
      });
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setToast({
        type: 'error',
        message: 'Enter your email address first, then request a password reset.',
      });
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
          redirectTo: 'https://app.raisethebaruk.com/activate-account?mode=recovery',
      });

      if (error) throw error;

      setToast({
        type: 'success',
        message: 'Password reset email sent. Check your inbox and follow the link to set a new password.',
      });
    } catch (error) {
      setToast({
        type: 'error',
        message: error?.message || 'Unable to send password reset email right now.',
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear validation error when user starts typing
    if (validation[name]) {
      setValidation({ ...validation, [name]: '' });
    }
  };

  return (
    <div className="min-h-screen bg-[#121212] flex flex-col lg:flex-row">
      {/* Desktop sidebar - Logo and branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1569947650381-faa14571844f?q=85')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/70 to-transparent" />
        <div className="relative z-10 flex flex-col justify-center items-center w-full h-full p-12">
          <div className="flex flex-col items-center">
            {/* Logo */}
            <div className="w-32 h-32 mb-8 flex items-center justify-center">
              <img 
                src="/logo.png" 
                alt="RTB" 
                className="w-full h-full object-contain drop-shadow-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextElementSibling) {
                    e.target.nextElementSibling.style.display = 'block';
                  }
                }}
              />
              <div style={{display: 'none'}} className="w-32 h-32 bg-gradient-to-br from-[#FF6600] to-[#FF8533] rounded-lg flex items-center justify-center">
                <span className="text-white text-3xl font-bold">RTB</span>
              </div>
            </div>
            
            <h1 className="font-heading text-6xl font-bold text-white uppercase tracking-tighter text-center mb-4">
              RAISE<br />THE BAR
            </h1>
            <p className="text-[#A1A1AA] text-lg text-center max-w-md">
              The operational control system for hospitality.
            </p>
          </div>
        </div>
      </div>

      {/* Login form */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8 form-mobile-safe">
        <div className="w-full max-w-md">
          <div className="card-rtb">
            {/* Mobile logo - shown only on smaller screens */}
            <div className="lg:hidden flex justify-center mb-6">
              <div className="w-24 h-24 flex items-center justify-center">
                <img 
                  src="/logo.png" 
                  alt="RTB" 
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextElementSibling) {
                      e.target.nextElementSibling.style.display = 'block';
                    }
                  }}
                />
                <div style={{display: 'none'}} className="w-24 h-24 bg-gradient-to-br from-[#FF6600] to-[#FF8533] rounded-lg flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">RTB</span>
                </div>
              </div>
            </div>

            {/* App name */}
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-2">
                RAISE THE BAR
              </h1>
              <p className="text-[#A1A1AA] text-sm">
                Sign in to your account
              </p>
            </div>

            {/* Toast notifications */}
            {toast && (
              <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
                toast.type === 'error' 
                  ? 'bg-red-500/10 border border-red-500/30' 
                  : toast.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/30'
                  : 'bg-blue-500/10 border border-blue-500/30'
              }`}>
                {toast.type === 'error' && <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />}
                {toast.type === 'success' && <CheckCircle size={20} className="text-green-500 flex-shrink-0 mt-0.5" />}
                <p className={`text-sm ${
                  toast.type === 'error' 
                    ? 'text-red-400' 
                    : toast.type === 'success'
                    ? 'text-green-400'
                    : 'text-blue-400'
                }`}>
                  {toast.message}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email field */}
              <div>
                <Label className="text-[#A1A1AA]">Email Address *</Label>
                <Input 
                  name="email" 
                  type="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  className={`input-rtb ${validation.email ? 'border-red-500' : ''}`}
                  placeholder="your@email.com"
                />
                {validation.email && (
                  <p className="text-red-400 text-xs mt-1">{validation.email}</p>
                )}
              </div>

              {/* Password field */}
              <div>
                <Label className="text-[#A1A1AA]">Password *</Label>
                <div className="relative">
                  <Input 
                    name="password" 
                    type={showPassword ? 'text' : 'password'} 
                    value={formData.password} 
                    onChange={handleChange} 
                    className={`input-rtb ${validation.password ? 'border-red-500' : ''}`}
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white transition"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {validation.password && (
                  <p className="text-red-400 text-xs mt-1">{validation.password}</p>
                )}
              </div>

              {/* Submit button */}
              <Button 
                type="submit" 
                disabled={isLoading} 
                className="btn-rtb-primary w-full bg-[#FF6600] hover:bg-[#FF8533] py-6 mt-6 disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={20} />
                    Signing In...
                  </>
                ) : (
                  <>
                    <LogIn className="mr-2" size={20} />
                    Sign In
                  </>
                )}
              </Button>

                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="w-full text-center text-sm text-[#A1A1AA] hover:text-[#FF8533] mt-1 underline underline-offset-4 transition"
                >
                  Reset password
                </button>
            </form>

            {/* RTB Marketing & Support */}
            <div className="mt-6 pt-6 border-t border-[#333] text-center">
              <p className="text-[#A1A1AA] text-sm">
                Looking to Raise The Bar? We would love to help.{' '}
                <br className="sm:hidden" />
                <a
                  href="https://raisethebaruk.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF6600] hover:text-[#FF8533] font-semibold transition"
                >
                  raisethebaruk.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;